import type { FeedbackDraftView } from "@esi/shared";
import { Check, MessageSquareWarning, X } from "lucide-react";
import { type ReactNode, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DismissReasons } from "@/components/DismissReasons";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSuggestFeedback } from "../account/preferences.ts";
import { ApiError } from "../api-errors.ts";
import { Link, MY_FEEDBACK_PATH } from "../router.tsx";
import { currentFeedbackContext } from "./context.ts";
import { useDismissDraft, useSendDraft } from "./drafts.ts";
import { FeedbackDraftDialog, failure } from "./FeedbackDialog.tsx";

/**
 * A feedback draft under the answer that drafted it (openspec change `feedback-from-conversation`,
 * design D3): the type and the description, who wrote it and that nothing from the conversation
 * goes with it, then Send, Edit (the feedback dialog, prefilled) and Dismiss. Dashed while
 * undecided, like a memory proposal; never a modal and never focus-stealing, so the composer
 * stays where the user left it. Only the user's click sends it.
 */
export function FeedbackDraftCard(props: {
  draft: FeedbackDraftView;
  /** False once the next turn started: a dismissal no longer asks why. */
  askWhy?: boolean | undefined;
}) {
  const { draft } = props;
  const { t } = useTranslation();
  const id = useId();
  const send = useSendDraft(draft);
  const { dismiss, reason } = useDismissDraft(draft);
  const stopSuggesting = useSuggestFeedback();
  const [editing, setEditing] = useState(false);
  const [stopped, setStopped] = useState(false);
  const editButton = useRef<HTMLButtonElement>(null);
  // Settled here and now (reasons offered), or before this card mounted (only said).
  const [decidedEarlier] = useState(draft.status !== "pending");
  // The menu context as the dialog would show it, read when the card is sent as drafted.
  const context = useMemo(() => currentFeedbackContext(), []);
  const noticed = draft.trigger === "noticed";
  // Decided here (the mutation) or elsewhere (the dialog's send, another tab: the cached draft).
  const status = send.isSuccess ? "sent" : dismiss.isSuccess ? "dismissed" : draft.status;
  // Mounted whatever the card's state: a send from the dialog settles the card under it, and
  // the dialog still shows its confirmation.
  const dialog = editing ? (
    <FeedbackDraftDialog
      draft={draft}
      open={editing}
      onClose={() => setEditing(false)}
      returnFocus={() => editButton.current}
    />
  ) : null;
  // One root for every state, the dialog first: settling the card never remounts the dialog.
  const settled = (body: ReactNode) => (
    <>
      {dialog}
      {body}
    </>
  );

  if (status === "dismissed") {
    return settled(
      <DismissReasons onReason={reason} offer={!decidedEarlier && props.askWhy !== false}>
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <X aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
          <span>
            {noticed ? t("feedbackDraft.dismissed") : t("feedbackDraft.dismissedRequested")}
          </span>
          {noticed && !decidedEarlier ? (
            stopped ? (
              <span>{t("feedbackDraft.stopped")}</span>
            ) : (
              <button
                type="button"
                disabled={stopSuggesting.isPending}
                onClick={() => stopSuggesting.mutate(false, { onSuccess: () => setStopped(true) })}
                className="text-accent-text underline-offset-4 hover:underline focus-visible:underline disabled:opacity-60"
              >
                {t("feedbackDraft.stopSuggesting")}
              </button>
            )
          ) : null}
        </span>
      </DismissReasons>,
    );
  }
  if (status === "sent") {
    return settled(<SentLine />);
  }

  const error = send.error ?? dismiss.error;
  // Decided in another tab, or expired: nothing left to do here.
  const gone =
    error instanceof ApiError && (error.status === 409 || (error.status === 404 && dismiss.error));
  const busy = send.isPending || dismiss.isPending;

  return settled(
    <section
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex flex-col gap-2.5 rounded-lg border border-dashed border-line-strong px-3.5 py-3",
        "transition-colors duration-(--duration-fast) ease-(--ease-out) focus-within:border-solid",
      )}
    >
      <div className="flex items-start gap-2.5">
        <MessageSquareWarning
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 id={`${id}-title`} className="text-[13px] font-medium text-fg-secondary">
            {noticed ? t("feedbackDraft.suggested") : t("feedbackDraft.requested")}
          </h3>
          <p className="text-sm leading-relaxed text-pretty text-fg">{draft.description}</p>
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-fg-muted">
            <span className="rounded-full border border-line px-2 py-px text-fg-secondary">
              {t(`feedback.types.${draft.type}`)}
            </span>
            <span>{t("feedbackDraft.drafted")}</span>
          </p>
        </div>
      </div>
      {error ? <InlineError>{gone ? t("feedbackDraft.gone") : failure(error)}</InlineError> : null}
      {gone ? null : (
        <div className="flex flex-wrap items-center gap-1.5 pl-6.5">
          <Button type="button" size="sm" disabled={busy} onClick={() => send.mutate({ context })}>
            {send.isPending ? t("feedbackDraft.sending") : t("feedbackDraft.send")}
          </Button>
          <Button
            ref={editButton}
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => setEditing(true)}
          >
            {t("feedbackDraft.edit")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            className="text-fg-secondary"
            onClick={() => dismiss.mutate()}
          >
            <X aria-hidden="true" strokeWidth={1.75} />
            {t("feedbackDraft.dismiss")}
          </Button>
        </div>
      )}
    </section>,
  );
}

/** The quiet line a sent draft settles into. */
function SentLine() {
  const { t } = useTranslation();
  return (
    <p role="status" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted">
      <Check aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      <span>{t("feedbackDraft.sent")}</span>
      <Link href={MY_FEEDBACK_PATH} className="text-accent-text underline-offset-4 hover:underline">
        {t("feedbackDraft.seeMine")}
      </Link>
    </p>
  );
}
